import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { ArrowLeft, MessageSquare, Plus, CheckCircle2, XCircle, Circle, Bell, UtensilsCrossed, Trash2, Pencil, KeyRound, Moon } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader, Pill } from "../components/common";
import ProgramCalendar from "./ProgramCalendar";

const TABS = ["Overview", "Program", "Calendar", "Progress", "Nutrition", "Notes", "Billing"];
const MEAL_TYPES = ["Breakfast", "Lunch", "Dinner", "Snack"];
const STATUSES = ["active", "trialing", "free", "past_due", "canceled"];
const blankMeal = () => ({ type: "Breakfast", name: "", description: "", calories: "", protein_g: "", items: [] });

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user.roles || [user.role]).includes("admin");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [note, setNote] = useState("");
  const [mealPlans, setMealPlans] = useState([]);
  const [plan, setPlan] = useState({ title: "", description: "", daily_calories: "", protein_g: "", carbs_g: "", fat_g: "", notes: "", meals: [blankMeal()] });
  const [editForm, setEditForm] = useState(null);
  const [packages, setPackages] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [templates, setTemplates] = useState([]);

  const load = () => api.get(`/coach/customers/${id}`).then(({ data }) => setData(data)).catch(() => navigate("/coach/customers"));
  const loadPlans = () => api.get(`/coach/meal-plans/${id}`).then(({ data }) => setMealPlans(data)).catch(() => {});
  useEffect(() => {
    load(); loadPlans();
    api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {});
    api.get("/workouts").then(({ data }) => setTemplates(data)).catch(() => {});
    if (isAdmin) api.get("/admin/coaches").then(({ data }) => setCoaches(data)).catch(() => {});
  }, [id]);
  if (!data) return <Loader />;
  const { customer: c, assignments, measurements, goals, payments, notes, package: pkg } = data;
  const weightData = measurements.filter((m) => m.type === "weight").map((m) => ({ date: m.date.slice(5), value: m.value }));

  const addDay = async (date, opts) => {
    try {
      await api.post("/assignments/day", { customer_id: id, date, workout_id: opts.workout_id || null, rest: !!opts.rest });
      toast.success(opts.rest ? "Rest day added." : "Workout added to schedule.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add to schedule."); }
  };

  const copyWeek = async (weekStart, weeks) => {
    try {
      const { data } = await api.post("/assignments/copy-week", { customer_id: id, week_start: weekStart, repeat_weeks: weeks });
      toast.success(`Copied ${data.sessions_per_week} session(s) across ${data.weeks} week(s).`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not copy week."); }
  };

  const removeAssignment = async (a) => {
    if (!window.confirm(`Remove "${a.workout_name}" (${a.scheduled_date}) from ${c.name}'s schedule?`)) return;
    try {
      await api.delete(`/assignments/${a.id}`);
      toast.success("Workout removed from schedule.");
      load();
    } catch (e) { toast.error("Could not remove workout."); }
  };

  const message = async () => {
    const { data: conv } = await api.post(`/conversations/start/${id}`);
    navigate("/coach/messages", { state: { conversationId: conv.id } });
  };

  const remindCheckin = async () => {
    const { data } = await api.post(`/coach/checkin-reminder/${id}`);
    toast.success(data.sent ? "Check-in reminder emailed!" : "Reminder logged (email not configured).");
  };

  const openEdit = () => {
    const c = data.customer;
    setEditForm({ name: c.name, email: c.email, password: "", subscription_status: c.subscription_status || "active", package_id: c.package_id || "", coach_id: c.coach_id || "", phone: c.phone || "", primary_goal: c.primary_goal || "" });
  };
  const saveEdit = async () => {
    if (!editForm.name || !editForm.email) return toast.error("Name and email are required.");
    setSavingEdit(true);
    try {
      const payload = { ...editForm, package_id: editForm.package_id || null, coach_id: editForm.coach_id || null, password: editForm.password || null };
      await api.put(`/admin/customers/${id}`, payload);
      toast.success("Customer updated!");
      setEditForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update."); }
    finally { setSavingEdit(false); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/customers/${id}`);
      toast.success("Customer deleted.");
      navigate("/coach/customers");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete."); setDeleting(false); }
  };

  const sendReset = async () => {
    try {
      const { data } = await api.post(`/coach/customers/${id}/send-reset`, { origin_url: window.location.origin });
      if (data.sent) toast.success(`Password reset link emailed to ${data.email}.`);
      else toast.error("Email isn't configured yet — add your SendGrid key in Settings to send reset emails.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send reset email."); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post("/coach/notes", { customer_id: id, text: note });
    setNote(""); toast.success("Private note saved."); load();
  };

  const setMeal = (i, patch) => setPlan((p) => ({ ...p, meals: p.meals.map((m, idx) => idx === i ? { ...m, ...patch } : m) }));
  const addMeal = () => setPlan((p) => ({ ...p, meals: [...p.meals, blankMeal()] }));
  const removeMeal = (i) => setPlan((p) => ({ ...p, meals: p.meals.filter((_, idx) => idx !== i) }));

  const savePlan = async () => {
    if (!plan.title.trim()) return toast.error("Add a plan title.");
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const payload = {
      customer_id: id, title: plan.title, description: plan.description, notes: plan.notes,
      daily_calories: num(plan.daily_calories), protein_g: num(plan.protein_g), carbs_g: num(plan.carbs_g), fat_g: num(plan.fat_g),
      meals: plan.meals.filter((m) => m.name.trim()).map((m) => ({
        type: m.type, name: m.name, description: m.description,
        calories: num(m.calories), protein_g: num(m.protein_g),
        items: typeof m.items === "string" ? m.items.split(",").map((s) => s.trim()).filter(Boolean) : (m.items || []),
      })),
    };
    await api.post("/coach/meal-plans", payload);
    toast.success("Meal plan shared with customer!");
    setPlan({ title: "", description: "", daily_calories: "", protein_g: "", carbs_g: "", fat_g: "", notes: "", meals: [blankMeal()] });
    loadPlans();
  };

  const deletePlan = async (mpId) => {
    if (!window.confirm("Delete this meal plan?")) return;
    await api.delete(`/coach/meal-plans/${mpId}`); toast.success("Meal plan deleted."); loadPlans();
  };

  const statusIcon = (s) => s === "completed" ? <CheckCircle2 className="w-4 h-4 text-success" /> : s === "missed" ? <XCircle className="w-4 h-4 text-brand" /> : s === "rest" ? <Moon className="w-4 h-4 text-tertiary" /> : <Circle className="w-4 h-4 text-tertiary" />;

  return (
    <div className="p-6 lg:p-8">
      <button data-testid="detail-back" onClick={() => navigate("/coach/customers")} className="flex items-center gap-2 text-secondary text-sm hover:text-white"><ArrowLeft className="w-4 h-4" /> Customers</button>
      <div className="flex flex-wrap items-center gap-4 mt-4">
        <div className="w-16 h-16 rounded-full bg-brand/20 flex items-center justify-center font-heading text-3xl text-brand">{c.name[0]}</div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-4xl">{c.name}</h1>
          <p className="text-secondary text-sm">{c.email} · {c.phone}</p>
          <div className="flex gap-1.5 mt-2 flex-wrap">{(c.tags || []).map((t) => <Pill key={t}>{t}</Pill>)}<Pill tone={c.subscription_status === "active" ? "success" : "warning"}>{c.subscription_status}</Pill></div>
        </div>
        <button data-testid="detail-edit" onClick={openEdit} className="px-4 py-2.5 border border-white/20 rounded-lg font-bold uppercase text-sm tracking-wide hover:border-white flex items-center gap-2"><Pencil className="w-4 h-4" /> Edit</button>
        <button data-testid="detail-delete" onClick={() => setConfirmDel(true)} className="px-4 py-2.5 border border-brand/40 text-brand rounded-lg font-bold uppercase text-sm tracking-wide hover:bg-brand/10 flex items-center gap-2"><Trash2 className="w-4 h-4" /> Delete</button>
        <button data-testid="detail-remind" onClick={remindCheckin} className="px-4 py-2.5 border border-white/20 rounded-lg font-bold uppercase text-sm tracking-wide hover:border-white flex items-center gap-2"><Bell className="w-4 h-4" /> Remind</button>
        <button data-testid="detail-message" onClick={message} className="px-5 py-2.5 bg-brand text-white font-bold uppercase text-sm tracking-wide rounded-lg hover:bg-brand-hover flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Message</button>
      </div>

      <div className="flex gap-1 mt-6 border-b border-border overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${tab === t ? "border-brand text-brand" : "border-transparent text-secondary hover:text-white"}`}>{t}</button>
        ))}
      </div>

      <div className="mt-6 max-w-3xl">
        {tab === "Overview" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Info label="Completion Rate" value={`${c.completion_rate}%`} />
            <Info label="Streak" value={`${c.streak || 0} days`} />
            <Info label="Primary Goal" value={c.primary_goal} />
            <Info label="Experience" value={c.experience} />
            <Info label="Environment" value={c.environment} />
            <Info label="Plan" value={pkg?.name || "—"} />
            <div className="card-surface rounded-xl p-4 sm:col-span-2"><p className="label-xs">Injuries / Limitations</p><p className="text-sm mt-1 text-secondary">{c.injuries || "None reported"}</p></div>
          </div>
        )}
        {tab === "Program" && (
          <div className="space-y-2">
            {assignments.length === 0 && <div className="card-surface rounded-xl p-8 text-center text-tertiary text-sm">No workouts assigned yet. Use the Workout Builder to assign one.</div>}
            {assignments.slice(0, 50).map((a) => (
              <div key={a.id} data-testid={`asn-row-${a.id}`} className="card-surface rounded-xl p-3 flex items-center gap-3">
                {statusIcon(a.status)}
                <div className="flex-1"><p className="font-semibold text-sm">{a.workout_name}</p><p className="text-xs text-tertiary">{a.scheduled_date}</p></div>
                {a.flagged && <Pill tone="danger">Pain</Pill>}
                <Pill>{a.status}</Pill>
                <button data-testid={`asn-remove-${a.id}`} onClick={() => removeAssignment(a)} title="Remove from schedule" className="p-1.5 text-tertiary hover:text-brand"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
        {tab === "Calendar" && (
          <ProgramCalendar assignments={assignments} templates={templates} onRemove={removeAssignment} onAdd={addDay} onCopyWeek={copyWeek} />
        )}
        {tab === "Progress" && (
          <div>
            <div className="card-surface rounded-xl p-5">
              <p className="label-xs">Weight Trend</p>
              <div className="h-48 mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weightData}><XAxis dataKey="date" stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} /><YAxis hide domain={["dataMin-2", "dataMax+2"]} /><Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #333", borderRadius: 8 }} /><Line type="monotone" dataKey="value" stroke="#FF3B30" strokeWidth={2.5} dot={false} /></LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {goals.map((g) => (<div key={g.id} className="card-surface rounded-xl p-4"><div className="flex justify-between"><p className="font-semibold">{g.title}</p><span className="font-num text-brand text-sm">{g.progress}%</span></div><div className="h-2 bg-white/10 rounded-full mt-2"><div className="h-full bg-brand rounded-full" style={{ width: `${g.progress}%` }} /></div></div>))}
            </div>
          </div>
        )}
        {tab === "Nutrition" && (
          <div>
            <p className="label-xs mb-2">Existing Plans</p>
            <div className="space-y-2">
              {mealPlans.map((mp) => (
                <div key={mp.id} className="card-surface rounded-xl p-4" data-testid="coach-meal-plan">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold flex items-center gap-2"><UtensilsCrossed className="w-4 h-4 text-success" /> {mp.title}</p>
                      <p className="text-xs text-tertiary mt-0.5">{mp.daily_calories || "—"} kcal · {mp.protein_g || "—"}g protein · {mp.meals?.length || 0} meals</p>
                    </div>
                    <button data-testid={`meal-plan-delete-${mp.id}`} onClick={() => deletePlan(mp.id)} className="text-tertiary hover:text-brand"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {mealPlans.length === 0 && <p className="text-tertiary text-sm">No meal plans yet.</p>}
            </div>

            <div className="card-surface rounded-xl p-5 mt-5">
              <h3 className="text-lg flex items-center gap-2"><UtensilsCrossed className="w-5 h-5 text-brand" /> New Meal Plan</h3>
              <div className="space-y-3 mt-4">
                <input data-testid="plan-title" value={plan.title} onChange={(e) => setPlan({ ...plan, title: e.target.value })} placeholder="Plan title (e.g. Lean Performance Plan)" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
                <input data-testid="plan-desc" value={plan.description} onChange={(e) => setPlan({ ...plan, description: e.target.value })} placeholder="Short description" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
                <div className="grid grid-cols-4 gap-2">
                  {[["daily_calories", "Calories"], ["protein_g", "Protein g"], ["carbs_g", "Carbs g"], ["fat_g", "Fat g"]].map(([k, l]) => (
                    <div key={k}><label className="label-xs">{l}</label><input data-testid={`plan-${k}`} type="number" value={plan[k]} onChange={(e) => setPlan({ ...plan, [k]: e.target.value })} className="w-full bg-surface-elevated border border-border rounded-lg px-2 py-2 text-sm mt-1 focus:outline-none focus:border-brand" /></div>
                  ))}
                </div>

                <p className="label-xs pt-2">Meals</p>
                {plan.meals.map((m, i) => (
                  <div key={i} className="border border-border rounded-lg p-3" data-testid={`plan-meal-${i}`}>
                    <div className="flex items-center gap-2">
                      <select value={m.type} onChange={(e) => setMeal(i, { type: e.target.value })} className="bg-surface-elevated border border-border rounded-lg px-2 py-1.5 text-sm">{MEAL_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
                      <input data-testid={`meal-name-${i}`} value={m.name} onChange={(e) => setMeal(i, { name: e.target.value })} placeholder="Meal name" className="flex-1 bg-surface-elevated border border-border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand" />
                      <button onClick={() => removeMeal(i)} className="text-tertiary hover:text-brand"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <input value={m.description} onChange={(e) => setMeal(i, { description: e.target.value })} placeholder="Description" className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-1.5 text-sm mt-2 focus:outline-none focus:border-brand" />
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <input type="number" value={m.calories} onChange={(e) => setMeal(i, { calories: e.target.value })} placeholder="Calories" className="bg-surface-elevated border border-border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand" />
                      <input type="number" value={m.protein_g} onChange={(e) => setMeal(i, { protein_g: e.target.value })} placeholder="Protein g" className="bg-surface-elevated border border-border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand" />
                    </div>
                    <input value={Array.isArray(m.items) ? m.items.join(", ") : m.items} onChange={(e) => setMeal(i, { items: e.target.value })} placeholder="Food items (comma separated)" className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-1.5 text-sm mt-2 focus:outline-none focus:border-brand" />
                  </div>
                ))}
                <button data-testid="plan-add-meal" onClick={addMeal} className="text-sm text-brand font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> Add meal</button>
                <textarea data-testid="plan-notes" value={plan.notes} onChange={(e) => setPlan({ ...plan, notes: e.target.value })} placeholder="Coach notes (hydration, timing, etc.)" rows={2} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
                <button data-testid="plan-save" onClick={savePlan} className="w-full py-3 bg-brand text-white font-bold uppercase tracking-wide rounded-lg hover:bg-brand-hover">Share Meal Plan with Customer</button>
              </div>
            </div>
          </div>
        )}
        {tab === "Notes" && (
          <div>
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 text-xs text-warning mb-4">Private coach notes — never visible to the customer.</div>
            <div className="flex gap-2"><input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note..." className="flex-1 bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" /><button data-testid="note-add" onClick={addNote} className="px-4 bg-brand text-white rounded-lg font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /></button></div>
            <div className="mt-4 space-y-2">{notes.map((n) => (<div key={n.id} className="card-surface rounded-xl p-3"><p className="text-sm">{n.text}</p><p className="text-xs text-tertiary mt-1">{new Date(n.created_at).toLocaleDateString()}</p></div>))}{notes.length === 0 && <p className="text-tertiary text-sm">No notes yet.</p>}</div>
          </div>
        )}
        {tab === "Billing" && (
          <div className="space-y-2">
            {payments.map((p) => (<div key={p.id} className="card-surface rounded-xl p-3 flex items-center justify-between"><div><p className="font-semibold text-sm">${p.amount} {p.currency?.toUpperCase()}</p><p className="text-xs text-tertiary">{new Date(p.created_at).toLocaleDateString()}</p></div><Pill tone={["paid", "complete"].includes(p.status) ? "success" : "danger"}>{p.status}</Pill></div>))}
            {payments.length === 0 && <p className="text-tertiary text-sm">No billing history.</p>}
          </div>
        )}
      </div>

      {editForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-5 z-[60]" onClick={() => !savingEdit && setEditForm(null)}>
          <div className="card-surface rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-3xl">Edit Customer</h2>
            <div className="space-y-3 mt-4">
              <input data-testid="edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Full name" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              <input data-testid="edit-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="Email" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              <input data-testid="edit-password" type="text" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Reset password (leave blank to keep)" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              <button type="button" data-testid="edit-send-reset" onClick={sendReset} className="w-full py-2 border border-white/20 rounded-lg text-sm font-semibold hover:border-white flex items-center justify-center gap-2"><KeyRound className="w-4 h-4" /> Email customer a reset link instead</button>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="label-xs block mb-1">Plan</label>
                  <select data-testid="edit-plan" value={editForm.package_id} onChange={(e) => setEditForm({ ...editForm, package_id: e.target.value })} className="w-full bg-surface-elevated border border-border rounded-lg px-2 py-2.5 text-sm">
                    <option value="">No plan</option>
                    {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div><label className="label-xs block mb-1">Status</label>
                  <select data-testid="edit-status" value={editForm.subscription_status} onChange={(e) => setEditForm({ ...editForm, subscription_status: e.target.value })} className="w-full bg-surface-elevated border border-border rounded-lg px-2 py-2.5 text-sm capitalize">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              {isAdmin && (
                <div><label className="label-xs block mb-1">Coach</label>
                  <select data-testid="edit-coach" value={editForm.coach_id} onChange={(e) => setEditForm({ ...editForm, coach_id: e.target.value })} className="w-full bg-surface-elevated border border-border rounded-lg px-2 py-2.5 text-sm">
                    <option value="">Unassigned</option>
                    {coaches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button data-testid="edit-save" onClick={saveEdit} disabled={savingEdit} className="flex-1 py-3 bg-brand text-white font-bold uppercase tracking-wide rounded-lg hover:bg-brand-hover disabled:opacity-60">{savingEdit ? "Saving…" : "Save Changes"}</button>
                <button onClick={() => setEditForm(null)} className="px-4 border border-white/20 rounded-lg">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {confirmDel && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-5 z-[60]" onClick={() => !deleting && setConfirmDel(false)}>
          <div className="card-surface rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-3xl">Delete customer?</h2>
            <p className="text-secondary text-sm mt-1">"{data.customer.name}" and all their workouts, messages, progress and notes will be permanently removed. This cannot be undone.</p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setConfirmDel(false)} disabled={deleting} className="flex-1 py-2.5 border border-white/20 rounded-lg font-semibold text-sm hover:border-white">Cancel</button>
              <button data-testid="detail-delete-confirm" onClick={doDelete} disabled={deleting} className="flex-1 py-2.5 bg-brand text-white rounded-lg font-bold text-sm hover:bg-brand-hover disabled:opacity-60">{deleting ? "Deleting…" : "Delete"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return <div className="card-surface rounded-xl p-4"><p className="label-xs">{label}</p><p className="font-semibold mt-1">{value}</p></div>;
}
